import React, { FC, useEffect, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KButton, KDropdown, KInput, KSpan, KTooltip } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { crawlers, users } from "../../../../../apis"
import { ExclusivityReleaseLog, ExclusivitySummary, UserLog } from "../../../../../types"
import { mapListToCamelCase } from "../../../../../utility"
import { toast } from "react-toastify"

import { connect } from "react-redux"
import { RootState } from "../../../../../store"
import { SetURLSearchParams } from "react-router-dom"
import Pagination from "../../../../ui/table/Pagination"
import { EXCLUSIVITY_LOG_ACTION } from "../../../../../constants"

type UserLogMap = {
  [key: string]: UserLog[]
}

const summaryColumnWidths: any = {
  ID: "4%",
  DATE: "10%",
  ARTISTS: "8%",
  CHECKED: "8%",
  FOUND: "8%",
  OPEN: "8%",
  NEW: "8%",
  UNDER_REVIEW: "9%",
  ARTIST_ASKED: "9%",
  ACTION_TAKEN: "9%",
  COMPLIANT: "9%",
  CLOSED: "8%",
  REMOVED: "8%",
  ACTION: "5%"
}

const logColumnWidths: any = {
  ID: "4%",
  RELEASE: "56%",
  "USER / ARTIST": "15%",
  LABEL: "15%",
  RELEASE_DATE: "10%"
}

const ActionOptions: KSelectOption[] = [
  { value: 0, value2: EXCLUSIVITY_LOG_ACTION.NEW, label: "New" },
  { value: 1, value2: EXCLUSIVITY_LOG_ACTION.UNDER_REVIEW, label: "Under Review" },
  { value: 2, value2: EXCLUSIVITY_LOG_ACTION.ARTIST_ASKED, label: "Artist Asked" },
  { value: 3, value2: EXCLUSIVITY_LOG_ACTION.ACTION_TAKEN, label: "Action Taken" },
  { value: 4, value2: EXCLUSIVITY_LOG_ACTION.COMPLIANT, label: "Compliant" },
  { value: 5, value2: EXCLUSIVITY_LOG_ACTION.CLOSED, label: "Closed" }
]

type AdminExclusivityLogProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
  headerDiv: JSX.Element
}

const AdminExclusivityLog: FC<AdminExclusivityLogProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [exclusivitySummaryList, setExclusivitySummaryList] = useState<ExclusivitySummary[]>([])
  const [count, setCount] = useState(0)
  const [selectedSummary, setSelectedSummary] = useState<ExclusivitySummary>()

  const [logList, setLogList] = useState<ExclusivityReleaseLog[]>([])
  const [logOffset, setLogOffset] = useState(0)
  const [logLimit, setLogLimit] = useState(100)
  const [logCount, setLogCount] = useState(0)
  const [openLogsMap, setOpenLogsMap] = useState<UserLogMap>({})
  const [summaryOpenLogsMap, setSummaryOpenLogsMap] = useState<UserLogMap>({})
  const [selectedAction, setSelectedAction] = useState<KSelectOption[]>([
    { value: 0, value2: EXCLUSIVITY_LOG_ACTION.NEW, label: "New" }
  ])
  const [searchText, setSearchText] = useState("")

  const fetchExclusivitySummaryList = (offset: number) => {
    setLoading(true)
    crawlers(`/exclusivity-logs/exclusivity_summaries/?offset=${offset}&limit=${limit}`, "GET").then((resp) => {
      setExclusivitySummaryList(mapListToCamelCase(resp.data.results))
      setCount(resp.data.count)
      setLoading(false)
    })
  }

  const fetchExclusivityLogList = (summaryId: number, offset: number) => {
    setLoading(true)
    const actionParam =
      selectedAction.length > 0 ? `&action=${selectedAction.map((action) => action.value2).join(",")}` : ""

    const searchParam = searchText ? `&search=${searchText}` : ""
    crawlers(
      `/exclusivity-logs/exclusivity_release_logs/?exclusivity_summary_id=${summaryId}&offset=${offset}&limit=${logLimit}${actionParam}${searchParam}`,
      "GET"
    ).then((resp) => {
      setLogList(mapListToCamelCase(resp.data.results))
      setLogCount(resp.data.count)
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchExclusivitySummaryList(0)
  }, [])

  useEffect(() => {
    if (!selectedSummary) return
    setLogOffset(0)
    fetchExclusivityLogList(selectedSummary.id, 0)
  }, [selectedSummary])

  const onActionClick = (releaseLog: ExclusivityReleaseLog, action?: string) => {
    const log = window.prompt("Enter log message for this action (optional):")

    if (log === null) {
      return
    }

    setLoading(true)
    crawlers(`/exclusivity-logs/update_exclusivity_release_action/`, "PUT", {
      id: releaseLog.id,
      action: action || "",
      log
    }).then((resp) => {
      setLoading(false)
      if (!resp.data.success) {
        toast.error(`Error: ${resp.data.message || "Failed to update action."}`)
        return
      }
      setLogList(
        logList.map((erl) => {
          if (erl.id === releaseLog.id) erl.action = action
          return erl
        })
      )
      toast.success("Success!")
    })
  }

  const renderActionColor = (releaseLog: ExclusivityReleaseLog) => {
    switch (releaseLog.action) {
      case EXCLUSIVITY_LOG_ACTION.UNDER_REVIEW:
      case EXCLUSIVITY_LOG_ACTION.ARTIST_ASKED:
        return "#ffeeba"
      case EXCLUSIVITY_LOG_ACTION.ACTION_TAKEN:
      case EXCLUSIVITY_LOG_ACTION.COMPLIANT:
      case EXCLUSIVITY_LOG_ACTION.CLOSED:
        return "#c3e6cb"
      default:
        return "#f5c6cb"
    }
  }

  const onClickLog = async (releaseLog?: ExclusivityReleaseLog) => {
    if (!releaseLog || !releaseLog.upc) {
      return
    }

    if (releaseLog.upc in openLogsMap) {
      delete openLogsMap[releaseLog.upc]
    } else {
      const resp = await users(`/user_log/?type=exclusivity_log_action&model=Upc&entity_id=${releaseLog.upc}`, "GET")

      const userLogList = mapListToCamelCase(resp.data.results)
      openLogsMap[releaseLog.upc] = userLogList
    }

    setOpenLogsMap({ ...openLogsMap })
  }

  const onClickSummaryLog = async (summary?: ExclusivitySummary) => {
    if (!summary) {
      return
    }

    if (summary.id in summaryOpenLogsMap) {
      delete summaryOpenLogsMap[summary.id]
    } else {
      const resp = await users(
        `/user_log/?type=exclusivity_log_action&secondary_model=ExclusivitySummary&secondary_id=${summary.id}&option=fill_exclusivity_upc_model`,
        "GET"
      )

      const userLogList = mapListToCamelCase(resp.data.results)
      summaryOpenLogsMap[summary.id] = userLogList
    }

    setSummaryOpenLogsMap({ ...summaryOpenLogsMap })
  }

  const renderActions = (releaseLog: ExclusivityReleaseLog) => {
    return (
      <KTooltip
        backgroundColor="black"
        position="bottom"
        content={
          <div className="flex flex-col gap-1">
            <KSpan color="white" text={releaseLog.log || "-"} />
          </div>
        }
        children={
          <div className="flex gap-2 mt-1">
            {ActionOptions.map((actionOption) => (
              <KButton
                key={`exclusivity_action_${releaseLog.id}_${actionOption.value2}`}
                width="65px"
                height="40px"
                text={actionOption.label}
                onClick={() => onActionClick(releaseLog, actionOption.value2)}
                background={releaseLog.action === actionOption.value2 ? "#000000da" : "#00000011"}
                textColor={releaseLog.action === actionOption.value2 ? "white" : "#666666"}
              />
            ))}
          </div>
        }
      />
    )
  }

  const renderExclusivitySummaries = () => {
    return (
      <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
        <div className="flex pb-2 gap-2" style={{ width: "100%" }}>
          {Object.keys(summaryColumnWidths).map((header) => {
            return (
              <div key={`header_${header}`} style={{ width: summaryColumnWidths[header] }}>
                <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
              </div>
            )
          })}
        </div>
        {exclusivitySummaryList.map((summary) => {
          return (
            <div
              key={`exclusivity_summary_${summary.id}`}
              data-testid="admin-exclusivity-summary-row"
              style={{ width: "100%" }}
              className="cursor-pointer"
              onClick={() => setSelectedSummary(summary)}
            >
              <div className="flex mt-3 pt-3 gap-2" style={{ width: "100%", borderTop: "1px dashed grey" }}>
                <div style={{ width: summaryColumnWidths.ID }} className="flex gap-1 items-center">
                  <KSpan text={summary.id?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.DATE }} className="flex gap-1 items-center">
                  <KSpan text={`${summary.date}`} />
                </div>
                <div style={{ width: summaryColumnWidths.ARTISTS }} className="flex gap-1 items-center">
                  <KSpan text={summary.artistCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.CHECKED }} className="flex gap-1 items-center">
                  <KSpan text={summary.checkedReleaseCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.FOUND }} className="flex gap-1 items-center">
                  <KSpan text={summary.foundReleaseCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.OPEN }} className="flex gap-1 items-center">
                  <KSpan text={summary.openCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.NEW }} className="flex gap-1 items-center">
                  <KSpan text={summary.newCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.UNDER_REVIEW }} className="flex gap-1 items-center">
                  <KSpan text={summary.underReviewCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.ARTIST_ASKED }} className="flex gap-1 items-center">
                  <KSpan text={summary.artistAskedCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.ACTION_TAKEN }} className="flex gap-1 items-center">
                  <KSpan text={summary.actionTakenCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.COMPLIANT }} className="flex gap-1 items-center">
                  <KSpan text={summary.compliantCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.CLOSED }} className="flex gap-1 items-center">
                  <KSpan text={summary.closedCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.REMOVED }} className="flex gap-1 items-center">
                  <KSpan text={summary.removedCount?.toString() || "-"} />
                </div>
                <div style={{ width: summaryColumnWidths.ACTION }} className="flex gap-1 items-center">
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      onClickSummaryLog(summary)
                    }}
                  >
                    <KButton background="transparent" text="Logs" height="28px" width="54px" onClick={() => {}} />
                  </div>
                </div>
              </div>
              {summary.id in summaryOpenLogsMap && (
                <div
                  className="flex flex-col  gap-4 mb-2 overflow-x-auto thin-scrollbar pb-2 pt-2"
                  style={{ width: "140vh" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {summaryOpenLogsMap[summary.id].length > 0 ? (
                    summaryOpenLogsMap[summary.id].map((userLog: UserLog) => {
                      return (
                        <div key={`track_log_${userLog.id}`} className="flex gap-2 flex-shrink-0 mt-1 ml-2">
                          <div className="flex flex-col">
                            <div className="flex flex-row gap-1">
                              <KSpan text={`${userLog.user?.firstName} ${userLog.user?.lastName}:`} fontWeight={500} />
                              <KSpan text={`${userLog.log || "-"}`} fontWeight={500} />
                            </div>
                            <div className="flex gap-10">
                              <KSpan
                                fontSize={11}
                                text={`Created: ${userLog.created?.substring(0, 19)}`}
                                fontWeight={500}
                              />
                              <KSpan fontSize={11} text={`Status: ${userLog.status}`} fontWeight={500} />
                              <KSpan fontSize={11} text={`Release UPC: ${userLog.release?.upc}`} fontWeight={500} />
                              <KSpan fontSize={11} text={`Release Title: ${userLog.release?.title}`} fontWeight={500} />
                            </div>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="ml-2">
                      <KSpan text="No Logs Available" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const renderExclusivityLogs = () => {
    return (
      <div className="pt-4 pb-4 flex flex-col">
        <div className="mb-6 flex justify-between">
          <div className="flex gap-4">
            <KDropdown
              placeholder="Select Action"
              menuWidth={170}
              isClearable
              isMulti
              closeMenuOnSelect={false}
              options={ActionOptions}
              selected={selectedAction}
              onSelect={(selected) => setSelectedAction(selected as KSelectOption[])}
            />
            <KInput width={120} onChange={(text) => setSearchText(text)} value={searchText} placeholder="Search UPC" />
            <KButton
              background="white"
              width="120px"
              text="Apply Filters"
              onClick={() => {
                if (!selectedSummary) return
                setLogOffset(0)
                fetchExclusivityLogList(selectedSummary.id, 0)
              }}
            />
          </div>
        </div>
        {/* Header */}
        <div className="flex pb-2 gap-2">
          {Object.keys(logColumnWidths).map((header) => (
            <div key={`log_header_${header}`} style={{ width: logColumnWidths[header] }}>
              <KSpan fontWeight={600} text={header.replaceAll("_", " ")} />
            </div>
          ))}
        </div>

        {/* Rows */}
        {logList.map((releaseLog) => (
          <div
            key={`exclusivity_log_${releaseLog.id}`}
            className="pt-2"
            style={{
              borderTop: "1px dashed grey",
              backgroundColor: renderActionColor(releaseLog)
            }}
          >
            <div className="flex gap-2 mb-2">
              {/* ID */}
              <div style={{ width: logColumnWidths.ID }}>
                <KSpan text={releaseLog.id?.toString() || "-"} />
              </div>

              {/* RELEASE */}
              <div style={{ width: logColumnWidths.RELEASE }}>
                <div className="flex gap-4">
                  <div>
                    {releaseLog.imageUrl && (
                      <img src={releaseLog.imageUrl} width={95} height={95} style={{ borderRadius: 5 }} />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <KSpan text={`${releaseLog.title || "-"}`} fontWeight={500} />
                    <div className="flex gap-2 mt-1">
                      <KSpan text={`UPC: ${releaseLog.upc || "-"}`} />
                      {releaseLog.spotifyAlbumId ? (
                        <img
                          className="cursor-pointer"
                          onClick={() => {
                            if (!releaseLog.spotifyAlbumId) return
                            window.open(`https://open.spotify.com/album/${releaseLog.spotifyAlbumId}`, "_blank")
                          }}
                          src={"/account_icons/social_media_icons/spotify.svg"}
                          alt={`${releaseLog.id}_spotify_link`}
                          style={{ width: 20, height: 20, minWidth: 20, minHeight: 20, cursor: "pointer" }}
                        />
                      ) : (
                        <KSpan text="-" />
                      )}
                    </div>
                    {renderActions(releaseLog)}
                  </div>
                </div>
              </div>

              {/* ARTIST */}
              <div style={{ width: logColumnWidths["USER / ARTIST"] }}>
                <div className="flex flex-col gap-1">
                  <KSpan
                    text={
                      releaseLog.user
                        ? `${releaseLog.user.firstName || ""} ${releaseLog.user.lastName || ""}`.trim() ||
                          releaseLog.user.username ||
                          "-"
                        : "-"
                    }
                    fontWeight={500}
                  />
                  <KSpan text={releaseLog.artists || "-"} />
                  <KButton
                    background="transparent"
                    text="Logs"
                    height="28px"
                    width="54px"
                    onClick={() => onClickLog(releaseLog)}
                  />
                </div>
              </div>

              {/* LABEL */}
              <div style={{ width: logColumnWidths.LABEL }}>
                <KSpan text={releaseLog.label || "-"} />
              </div>

              {/* RELEASE DATE */}
              <div style={{ width: logColumnWidths.RELEASE_DATE }}>
                <KSpan text={releaseLog.releaseDate || "-"} />
              </div>
            </div>
            {releaseLog.upc && releaseLog.upc in openLogsMap && (
              <div className="flex flex-col  gap-4 mb-2 overflow-x-auto thin-scrollbar pb-2" style={{ width: "140vh" }}>
                {openLogsMap[releaseLog.upc].length > 0 ? (
                  openLogsMap[releaseLog.upc].map((userLog: UserLog) => {
                    return (
                      <div key={`track_log_${userLog.id}`} className="flex gap-2 flex-shrink-0 mt-1 ml-2">
                        <div className="flex flex-col">
                          <div className="flex flex-row gap-1">
                            <KSpan text={`${userLog.user?.firstName} ${userLog.user?.lastName}:`} fontWeight={500} />
                            <KSpan text={`${userLog.log || "-"}`} fontWeight={500} />
                          </div>
                          <div className="flex gap-10">
                            <KSpan
                              fontSize={11}
                              text={`Created: ${userLog.created?.substring(0, 19)}`}
                              fontWeight={500}
                            />
                            <KSpan fontSize={11} text={`Status: ${userLog.status}`} fontWeight={500} />
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="ml-2">
                    <KSpan text="No Logs Available" />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  const onClickPagination = (pageNumber: number) => {
    setOffset((pageNumber - 1) * limit)
    fetchExclusivitySummaryList((pageNumber - 1) * limit)
  }

  const onClickLogPagination = (pageNumber: number) => {
    if (!selectedSummary) return
    setLogOffset((pageNumber - 1) * logLimit)
    fetchExclusivityLogList(selectedSummary.id, (pageNumber - 1) * logLimit)
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        {props.headerDiv}
        {selectedSummary && (
          <div className="flex gap-4">
            <div style={{ width: "200px", display: "flex", alignItems: "center", gap: "5px" }}>
              <KSpan text={`Selected Date: ${selectedSummary.date}`} />
            </div>
            <KButton
              width="200px"
              height="35px"
              text="Back to Exclusivity Logs"
              onClick={() => setSelectedSummary(undefined)}
            />
          </div>
        )}
      </div>
      {selectedSummary ? renderExclusivityLogs() : renderExclusivitySummaries()}
      {!selectedSummary && (
        <div>
          <Pagination
            key={""}
            hidden={false}
            size={"sm"}
            pageCount={Math.ceil(count / limit)}
            currentPage={offset / limit + 1}
            onClickPage={onClickPagination}
          />
        </div>
      )}
      {selectedSummary && (
        <div>
          <Pagination
            key={""}
            hidden={false}
            size={"sm"}
            pageCount={Math.ceil(logCount / logLimit)}
            currentPage={logOffset / logLimit + 1}
            onClickPage={onClickLogPagination}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(AdminExclusivityLog)
